import { Link } from '@tanstack/react-router'
import { addMonths, format, isSameDay, isSameMonth, startOfMonth } from 'date-fns'
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { useMemo } from 'react'
import {
  buildMonthGrid,
  type CalendarEntry,
  type CalendarEventState,
  dayKey,
  groupEntriesByDay,
} from '~/features/events/lib/event-calendar'
import { formatEventClock, formatEventLongDay } from '~/features/events/lib/event-date-format'
import * as m from '~/paraglide/messages'
import { dateFnsLocale } from '~/shared/lib/date-locale'
import { cn } from '~/shared/lib/utils'
import type { Event } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '~/shared/ui/popover'

const CHIPS_PER_DAY = 3

const STATE_CHIP: Record<CalendarEventState, string> = {
  upcoming: 'bg-brand-subtle text-brand-subtle-foreground hover:brightness-95',
  past: 'bg-muted text-muted-foreground hover:text-foreground',
  draft: 'border border-dashed border-foreground/25 text-foreground/80 hover:bg-foreground/[0.06]',
  canceled: 'bg-destructive/10 text-destructive line-through decoration-destructive/60',
}

const STATE_DOT: Record<CalendarEventState, string> = {
  upcoming: 'bg-brand',
  past: 'bg-muted-foreground/50',
  draft: 'border border-dashed border-foreground/50',
  canceled: 'bg-destructive',
}

interface EventCalendarProps {
  month: Date
  onMonthChange: (month: Date) => void
  events: Event[]
  isFetching: boolean
  now: Date
}

export function EventCalendar({
  month,
  onMonthChange,
  events,
  isFetching,
  now,
}: EventCalendarProps) {
  const locale = dateFnsLocale()
  const days = useMemo(() => buildMonthGrid(month), [month])
  const byDay = useMemo(() => groupEntriesByDay(events, now), [events, now])
  const showsCurrentMonth = isSameMonth(month, now)

  return (
    <section className="space-y-3" aria-label={format(month, 'LLLL yyyy', { locale })}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-lg font-semibold capitalize tracking-tight">
          {format(month, 'LLLL yyyy', { locale })}
        </h2>
        {isFetching && (
          <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden="true" />
        )}
        <Button
          variant="outline"
          size="sm"
          disabled={showsCurrentMonth}
          onClick={() => onMonthChange(startOfMonth(now))}
        >
          {m.today()}
        </Button>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={m.eventsCalendarPreviousMonth()}
            onClick={() => onMonthChange(addMonths(month, -1))}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={m.eventsCalendarNextMonth()}
            onClick={() => onMonthChange(addMonths(month, 1))}
          >
            <ChevronRight />
          </Button>
        </div>
      </div>

      <MonthGrid days={days} month={month} byDay={byDay} now={now} />
      <MonthAgenda days={days} month={month} byDay={byDay} now={now} />
    </section>
  )
}

interface MonthViewProps {
  days: Date[]
  month: Date
  byDay: Map<string, CalendarEntry[]>
  now: Date
}

function MonthGrid({ days, month, byDay, now }: MonthViewProps) {
  const locale = dateFnsLocale()
  return (
    <div className="hidden overflow-hidden rounded-xl border bg-card md:block">
      <div className="grid grid-cols-7 border-b bg-muted/40">
        {days.slice(0, 7).map((day) => (
          <div
            key={day.getDay()}
            className="px-2 py-1.5 text-xs font-medium text-muted-foreground"
            aria-hidden="true"
          >
            {format(day, 'EEEEEE', { locale })}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const entries = byDay.get(dayKey(day)) ?? []
          const inMonth = isSameMonth(day, month)
          const isToday = isSameDay(day, now)
          const hidden = entries.length - CHIPS_PER_DAY
          const shown = hidden > 0 ? entries.slice(0, CHIPS_PER_DAY - 1) : entries
          return (
            <div
              key={dayKey(day)}
              className={cn(
                'flex min-h-28 min-w-0 flex-col gap-1 border-b border-r p-1.5 [&:nth-child(7n)]:border-r-0',
                !inMonth && 'bg-muted/30',
              )}
            >
              <span
                className={cn(
                  'flex size-6 items-center justify-center rounded-full text-xs tabular-nums',
                  isToday && 'bg-foreground font-semibold text-background',
                  !isToday && !inMonth && 'text-muted-foreground/60',
                )}
              >
                <time dateTime={dayKey(day)}>{format(day, 'd')}</time>
              </span>
              <div className={cn('flex min-w-0 flex-col gap-0.5', !inMonth && 'opacity-60')}>
                {shown.map((entry) => (
                  <EventChip key={entry.key} entry={entry} />
                ))}
                {hidden > 0 && <MoreEvents day={day} entries={entries} count={hidden + 1} />}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function entryTime(entry: CalendarEntry) {
  return entry.isAllDay ? m.allDay() : formatEventClock(entry.start)
}

function EventChip({ entry }: { entry: CalendarEntry }) {
  return (
    <Link
      to="/dashboard/events/$eventId"
      params={{ eventId: entry.event.id }}
      title={`${entryTime(entry)} ${entry.event.title}`}
      className={cn(
        'flex min-w-0 items-baseline gap-1 rounded-md px-1.5 py-0.5 text-xs transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        STATE_CHIP[entry.state],
      )}
    >
      <span className="shrink-0 tabular-nums opacity-75">{entryTime(entry)}</span>
      <span className="truncate font-medium">{entry.event.title}</span>
    </Link>
  )
}

function MoreEvents({
  day,
  entries,
  count,
}: {
  day: Date
  entries: CalendarEntry[]
  count: number
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded-md px-1.5 py-0.5 text-left text-xs font-medium text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {m.eventsCalendarMore({ count: String(count) })}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 space-y-2 p-3">
        <p className="text-xs font-medium text-muted-foreground">{formatEventLongDay(day)}</p>
        <div className="flex flex-col gap-1">
          {entries.map((entry) => (
            <EventChip key={entry.key} entry={entry} />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function MonthAgenda({ days, month, byDay }: MonthViewProps) {
  const monthDays = days.filter((day) => isSameMonth(day, month) && byDay.has(dayKey(day)))

  if (monthDays.length === 0) {
    return (
      <p className="rounded-xl border bg-card px-4 py-8 text-center text-sm text-muted-foreground md:hidden">
        {m.eventsCalendarNoEventsThisMonth()}
      </p>
    )
  }

  return (
    <ol className="space-y-4 md:hidden">
      {monthDays.map((day) => (
        <li key={dayKey(day)} className="space-y-1.5">
          <h3 className="text-xs font-medium text-muted-foreground">
            <time dateTime={dayKey(day)}>{formatEventLongDay(day)}</time>
          </h3>
          <ul className="divide-y overflow-hidden rounded-xl border bg-card">
            {(byDay.get(dayKey(day)) ?? []).map((entry) => (
              <li key={entry.key}>
                <Link
                  to="/dashboard/events/$eventId"
                  params={{ eventId: entry.event.id }}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm transition-colors hover:bg-foreground/[0.04] focus-visible:bg-foreground/[0.04] focus-visible:outline-none"
                >
                  <span
                    className={cn('size-2 shrink-0 rounded-full', STATE_DOT[entry.state])}
                    aria-hidden="true"
                  />
                  <span className="w-16 shrink-0 text-xs tabular-nums text-muted-foreground">
                    {entryTime(entry)}
                  </span>
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate font-medium',
                      entry.state === 'past' && 'text-muted-foreground',
                      entry.state === 'canceled' && 'text-destructive line-through',
                    )}
                  >
                    {entry.event.title}
                  </span>
                  <AgendaStateLabel state={entry.state} />
                </Link>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ol>
  )
}

function AgendaStateLabel({ state }: { state: CalendarEventState }) {
  if (state === 'draft') {
    return <span className="shrink-0 text-xs text-muted-foreground">{m.draft()}</span>
  }
  if (state === 'canceled') {
    return <span className="shrink-0 text-xs text-destructive">{m.canceled()}</span>
  }
  return null
}
